<template>
    <div class="page-modules-editor">
        <input type="hidden" :name="inputName" :value="modulesJson" />

        <div class="page-modules-toolbar clearfix">
            <div class="btn-group">
                <button
                    v-for="type in moduleTypes"
                    :key="type"
                    type="button"
                    class="btn btn-primary btn-sm"
                    @click="addModule(type)"
                >
                    <i class="fa fa-plus"></i> {{ labels['module_type_' + type] || type }}
                </button>
            </div>
            <div class="pull-right">
                <label class="control-label" style="margin-right: 8px;">
                    {{ labels.module_preview_breakpoint }}
                </label>
                <select
                    v-model="activeBreakpoint"
                    class="form-control input-sm"
                    style="display: inline-block; width: auto;"
                    @change="onBreakpointChange"
                >
                    <option v-for="bp in breakpoints" :key="bp" :value="bp">
                        {{ labels['module_breakpoint_' + bp] || bp }}
                    </option>
                </select>
            </div>
        </div>

        <p class="help-block" style="margin-top: 10px;">
            {{ labels.module_visual_help }}
        </p>

        <div v-if="!modules.length" class="alert alert-info">
            {{ labels.modules_empty }}
        </div>

        <div
            v-if="modules.length"
            :key="gridKey"
            ref="grid"
            class="grid-stack page-modules-grid"
        >
            <div
                v-for="module in modules"
                :key="module.id"
                class="grid-stack-item"
                :data-module-id="module.id"
                :data-gs-x="module.layout[activeBreakpoint].offset"
                :data-gs-y="module.layout[activeBreakpoint].y"
                :data-gs-width="module.layout[activeBreakpoint].col"
                :data-gs-height="module.layout[activeBreakpoint].h"
            >
                <div
                    class="grid-stack-item-content page-module-widget"
                    :class="{ 'page-module-widget--image': module.type === 'image' }"
                    @mousedown="onWidgetMouseDown"
                    @click="onWidgetClick(module, $event)"
                >
                    <div class="page-module-widget-header">
                        <strong>{{ labels['module_type_' + module.type] || module.type }}</strong>
                        <button
                            type="button"
                            class="btn btn-danger btn-xs page-module-widget-remove"
                            @click.stop.prevent="removeModuleById(module.id)"
                            :title="labels.module_remove"
                        >
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <div
                        v-if="module.type === 'image'"
                        class="page-module-image-frame"
                    >
                        <img
                            v-if="module.data.url"
                            class="page-module-image-cover"
                            :class="{
                                'is-dragging': focalDragModuleId === module.id,
                                'no-pan': !imageCanPan(module),
                            }"
                            :src="module.data.url"
                            :alt="module.data.alt || ''"
                            :style="imageObjectPositionStyle(module)"
                            draggable="false"
                            @load="onImagePreviewLoad(module, $event)"
                            @pointerdown="onImageFocalPointerDown(module, $event)"
                        />
                        <div v-else class="page-module-image-empty text-muted">
                            {{ module.data.media_id ? ('media #' + module.data.media_id) : '—' }}
                        </div>
                    </div>
                    <div v-else class="page-module-widget-body" v-html="previewSnippet(module)"></div>
                    <div class="page-module-widget-meta text-muted">
                        {{ module.layout[activeBreakpoint].col }}/12
                        · x{{ module.layout[activeBreakpoint].offset }}
                        · h{{ module.layout[activeBreakpoint].h }}
                    </div>
                </div>
            </div>
        </div>

        <div
            ref="modal"
            class="modal fade page-module-edit-modal"
            tabindex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content" v-if="draft">
                    <div class="modal-header">
                        <button type="button" class="close" @click="closeModal">
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <h4 class="modal-title">
                            {{ labels.module_edit }} —
                            {{ labels['module_type_' + draft.type] || draft.type }}
                        </h4>
                    </div>
                    <div class="modal-body">
                        <div v-if="draft.type === 'html'" class="form-group">
                            <label class="control-label">{{ labels.module_body }}</label>
                            <textarea class="form-control" rows="8" v-model="draft.data.body"></textarea>
                        </div>

                        <div v-else-if="draft.type === 'text'" class="form-group">
                            <label class="control-label">{{ labels.module_body }}</label>
                            <div ref="modalSummernote" class="page-module-summernote"></div>
                        </div>

                        <div v-else-if="draft.type === 'image'" class="page-module-image-fields">
                            <div class="form-group">
                                <label class="control-label">{{ labels.module_image_source }}</label>
                                <div class="btn-group btn-group-sm" style="display: block;">
                                    <label class="btn btn-default" :class="{ active: imageSourceMode === 'url' }">
                                        <input type="radio" v-model="imageSourceMode" value="url" style="margin-right: 4px;">
                                        {{ labels.module_image_source_url }}
                                    </label>
                                    <label class="btn btn-default" :class="{ active: imageSourceMode === 'library' }">
                                        <input type="radio" v-model="imageSourceMode" value="library" style="margin-right: 4px;">
                                        {{ labels.module_image_source_library }}
                                    </label>
                                    <label class="btn btn-default" :class="{ active: imageSourceMode === 'upload' }">
                                        <input type="radio" v-model="imageSourceMode" value="upload" style="margin-right: 4px;">
                                        {{ labels.module_image_source_upload }}
                                    </label>
                                </div>
                            </div>

                            <div v-if="imageSourceMode === 'url'" class="form-group">
                                <label class="control-label">{{ labels.module_url }}</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="draft.data.url"
                                    @input="onImageUrlInput"
                                    placeholder="https://..."
                                />
                            </div>

                            <div v-else-if="imageSourceMode === 'library'" class="form-group">
                                <label class="control-label">{{ labels.module_image_library }}</label>
                                <select class="form-control" v-model="selectedLibraryMediaId" @change="onLibrarySelect">
                                    <option :value="null">{{ labels.module_image_library_empty }}</option>
                                    <option
                                        v-for="image in libraryImages"
                                        :key="image.id"
                                        :value="image.id"
                                    >
                                        {{ image.label }}
                                    </option>
                                </select>
                                <p v-if="libraryLoading" class="help-block">{{ labels.module_image_loading }}</p>
                                <p v-if="libraryError" class="help-block text-danger">{{ libraryError }}</p>
                            </div>

                            <div v-else class="form-group">
                                <label class="control-label">{{ labels.module_image_upload }}</label>
                                <input
                                    type="file"
                                    class="form-control"
                                    accept="image/*"
                                    @change="onImageUpload"
                                    :disabled="uploadInProgress"
                                />
                                <p v-if="uploadInProgress" class="help-block">{{ labels.module_image_uploading }}</p>
                                <p v-if="uploadError" class="help-block text-danger">{{ uploadError }}</p>
                            </div>

                            <div v-if="imagePreviewUrl" class="form-group">
                                <label class="control-label">{{ labels.module_image_preview }}</label>
                                <div>
                                    <img
                                        :src="imagePreviewUrl"
                                        :alt="draft.data.alt || ''"
                                        class="page-module-image-modal-full"
                                    >
                                </div>
                                <p class="help-block">{{ labels.module_image_focal_help }}</p>
                            </div>

                            <div class="form-group">
                                <label class="control-label">{{ labels.module_alt }}</label>
                                <input type="text" class="form-control" v-model="draft.data.alt" />
                            </div>
                        </div>

                        <div v-else-if="draft.type === 'video'" class="row">
                            <div class="col-md-3">
                                <div class="form-group">
                                    <label class="control-label">{{ labels.module_video_source }}</label>
                                    <select class="form-control" v-model="draft.data.source">
                                        <option value="url">{{ labels.module_video_source_url }}</option>
                                        <option value="media">{{ labels.module_video_source_media }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="form-group">
                                    <label class="control-label">{{ labels.module_url }}</label>
                                    <input type="text" class="form-control" v-model="draft.data.url" />
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="form-group">
                                    <label class="control-label">{{ labels.module_media_id }}</label>
                                    <input type="number" class="form-control" v-model.number="draft.data.media_id" />
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="form-group">
                                    <label class="control-label">{{ labels.module_poster }}</label>
                                    <input type="text" class="form-control" v-model="draft.data.poster" />
                                </div>
                            </div>
                        </div>

                        <div v-else-if="draft.type === 'accordion'" class="page-module-accordion-fields">
                            <label class="control-label">{{ labels.module_accordion_items }}</label>
                            <div
                                v-for="(item, itemIndex) in draft.data.items"
                                :key="item.id"
                                class="panel panel-default"
                                style="margin-bottom: 10px;"
                            >
                                <div class="panel-heading clearfix">
                                    <strong>#{{ itemIndex + 1 }}</strong>
                                    <div class="pull-right btn-group">
                                        <button
                                            type="button"
                                            class="btn btn-default btn-xs"
                                            :disabled="itemIndex === 0"
                                            @click="moveAccordionItem(itemIndex, -1)"
                                            :title="labels.module_accordion_move_up"
                                        >
                                            <i class="fa fa-arrow-up"></i>
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-default btn-xs"
                                            :disabled="itemIndex === draft.data.items.length - 1"
                                            @click="moveAccordionItem(itemIndex, 1)"
                                            :title="labels.module_accordion_move_down"
                                        >
                                            <i class="fa fa-arrow-down"></i>
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-danger btn-xs"
                                            @click="removeAccordionItem(itemIndex)"
                                            :title="labels.module_accordion_remove_item"
                                        >
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="panel-body">
                                    <div class="form-group">
                                        <label class="control-label">{{ labels.module_accordion_item_title }}</label>
                                        <input type="text" class="form-control" v-model="item.title" />
                                    </div>
                                    <div class="form-group" style="margin-bottom: 0;">
                                        <label class="control-label">{{ labels.module_accordion_item_body }}</label>
                                        <div
                                            class="page-module-summernote accordion-item-summernote"
                                            :data-item-index="itemIndex"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            <button type="button" class="btn btn-default btn-sm" @click="addAccordionItem">
                                <i class="fa fa-plus"></i> {{ labels.module_accordion_add_item }}
                            </button>
                        </div>

                        <div v-else-if="draft.type === 'faq'" class="form-group">
                            <label class="control-label">{{ labels.module_faq_categories }}</label>
                            <select
                                v-if="faqCategories.length"
                                class="form-control"
                                multiple
                                size="8"
                                v-model="draftCategoryIds"
                            >
                                <option
                                    v-for="category in faqCategories"
                                    :key="category.id"
                                    :value="String(category.id)"
                                >
                                    {{ category.title }}
                                </option>
                            </select>
                            <p v-else class="help-block">{{ labels.module_faq_categories_empty }}</p>
                            <p class="help-block">{{ labels.module_faq_categories_help }}</p>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-default" @click="closeModal">
                            {{ labels.module_cancel || 'Annulla' }}
                        </button>
                        <button type="button" class="btn btn-danger pull-left" @click="removeEditingModule">
                            {{ labels.module_remove }}
                        </button>
                        <button type="button" class="btn btn-success" @click="saveModal">
                            {{ labels.module_save || 'Salva' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const DEFAULT_LAYOUT = {
    mobile: { col: 12, offset: 0, y: 0, h: 4 },
    small: { col: 12, offset: 0, y: 0, h: 4 },
    desktop: { col: 12, offset: 0, y: 0, h: 4 },
};

function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function emptyData(type) {
    switch (type) {
        case 'text':
        case 'html':
            return { body: '' };
        case 'image':
            return {
                source: 'url',
                media_id: null,
                alt: '',
                url: null,
                object_position_x: 50,
                object_position_y: 50,
            };
        case 'video':
            return { source: 'url', url: null, media_id: null, poster: null };
        case 'accordion':
            return { items: [] };
        case 'faq':
            return { category_ids: [] };
        default:
            return {};
    }
}

function normalizeBreakpointLayout(layout) {
    let col = parseInt(layout?.col, 10);
    let offset = parseInt(layout?.offset, 10);
    let y = parseInt(layout?.y, 10);
    let h = parseInt(layout?.h, 10);

    if (Number.isNaN(col)) col = 12;
    if (Number.isNaN(offset)) offset = 0;
    if (Number.isNaN(y)) y = 0;
    if (Number.isNaN(h) || h < 1) h = 4;

    col = Math.max(1, Math.min(12, col));
    offset = Math.max(0, Math.min(11, offset));
    if (col + offset > 12) {
        offset = 12 - col;
    }
    y = Math.max(0, y);
    h = Math.max(1, Math.min(20, h));

    return { col, offset, y, h };
}

function packMissingPositions(modules, breakpoint) {
    let cursorX = 0;
    let cursorY = 0;
    let rowH = 1;

    modules.forEach((module) => {
        const layout = module.layout[breakpoint];
        const hasY = layout.y !== undefined && layout.y !== null && !Number.isNaN(parseInt(layout.y, 10));

        if (!hasY) {
            if (cursorX + layout.col > 12) {
                cursorX = 0;
                cursorY += rowH;
                rowH = 1;
            }
            layout.offset = cursorX;
            layout.y = cursorY;
            layout.h = layout.h || 2;
            cursorX += layout.col;
            rowH = Math.max(rowH, layout.h);
        } else {
            module.layout[breakpoint] = normalizeBreakpointLayout(layout);
        }
    });
}

export default {
    name: 'PageModulesEditor',
    props: {
        initialModules: {
            type: [Array, String],
            default: () => [],
        },
        inputName: {
            type: String,
            default: 'modules',
        },
        labels: {
            type: Object,
            default: () => ({}),
        },
        pageId: {
            type: [Number, String],
            default: null,
        },
        pageModel: {
            type: String,
            default: 'App\\Models\\Admin\\Page',
        },
        operation: {
            type: String,
            default: 'create',
        },
        uploadUrl: {
            type: String,
            default: '',
        },
        mediaLibraryUrl: {
            type: String,
            default: '',
        },
        faqCategories: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            modules: [],
            moduleTypes: ['text', 'image', 'video', 'html', 'accordion', 'faq'],
            breakpoints: ['mobile', 'small', 'desktop'],
            activeBreakpoint: 'desktop',
            grid: null,
            suppressGridSync: false,
            interactionMoved: false,
            mouseDownPos: null,
            draft: null,
            editingId: null,
            modalSummernoteReady: false,
            rebuildTimer: null,
            gridEpoch: 0,
            imageSourceMode: 'url',
            libraryImages: [],
            libraryLoading: false,
            libraryError: null,
            libraryLoaded: false,
            selectedLibraryMediaId: null,
            uploadInProgress: false,
            uploadError: null,
            draftCategoryIds: [],
            focalDragModuleId: null,
            imageOverflowById: {},
        };
    },
    computed: {
        modulesJson() {
            return JSON.stringify(this.modulesForSave());
        },
        gridKey() {
            return [
                this.activeBreakpoint,
                this.gridEpoch,
                this.modules.map((m) => m.id).join(','),
            ].join('|');
        },
        imagePreviewUrl() {
            if (!this.draft || this.draft.type !== 'image') {
                return null;
            }
            return this.draft.data.url || null;
        },
    },
    watch: {
        draftCategoryIds(ids) {
            if (!this.draft || this.draft.type !== 'faq') {
                return;
            }
            this.draft.data.category_ids = (ids || [])
                .map((id) => parseInt(id, 10))
                .filter((id) => !Number.isNaN(id) && id > 0);
        },
        imageSourceMode(mode) {
            if (!this.draft || this.draft.type !== 'image') {
                return;
            }
            this.draft.data.source = mode;
            if (mode === 'library') {
                this.loadLibraryImages();
            }
            if (mode === 'url') {
                this.draft.data.media_id = null;
                this.selectedLibraryMediaId = null;
            }
        },
    },
    mounted() {
        this.modules = this.parseInitialModules(this.initialModules);
        this.breakpoints.forEach((bp) => packMissingPositions(this.modules, bp));
        this.$nextTick(() => this.initGrid());
    },
    beforeUnmount() {
        this.destroyGrid();
        this.destroyModalSummernote();
        if (this.$refs.modal && window.$) {
            $(this.$refs.modal).off('.pageModules');
            $(this.$refs.modal).modal('hide');
        }
    },
    methods: {
        parseInitialModules(value) {
            let modules = value;
            if (typeof modules === 'string') {
                try {
                    modules = JSON.parse(modules);
                } catch (e) {
                    modules = [];
                }
            }
            if (!Array.isArray(modules)) {
                return [];
            }

            return modules.map((module) => ({
                id: module.id || uuid(),
                type: module.type,
                layout: {
                    mobile: normalizeBreakpointLayout(module.layout?.mobile || DEFAULT_LAYOUT.mobile),
                    small: normalizeBreakpointLayout(module.layout?.small || DEFAULT_LAYOUT.small),
                    desktop: normalizeBreakpointLayout(module.layout?.desktop || DEFAULT_LAYOUT.desktop),
                },
                data: { ...emptyData(module.type), ...(module.data || {}) },
            }));
        },
        initGrid() {
            if (!this.$refs.grid || typeof window.GridStack === 'undefined' || !window.GridStack.init) {
                return;
            }

            this.destroyGrid();

            this.grid = window.GridStack.init(
                {
                    column: 12,
                    cellHeight: 80,
                    verticalMargin: 10,
                    animate: true,
                    float: true,
                    minRow: 2,
                    disableOneColumnMode: true,
                    resizable: {
                        handles: 'se',
                        autoHide: false,
                    },
                    draggable: {
                        handle: '.page-module-widget',
                        cancel: '.page-module-widget-remove, .page-module-image-frame, .page-module-image-cover, button, a, input, textarea, select',
                        scroll: true,
                    },
                },
                this.$refs.grid
            );

            this.grid.on('dragstart', () => {
                this.interactionMoved = true;
            });
            this.grid.on('resizestart', () => {
                this.interactionMoved = true;
            });
            this.grid.on('change', (event, items) => {
                if (this.suppressGridSync || !items) {
                    return;
                }
                items.forEach((item) => {
                    const rawEl = item.el;
                    const el = rawEl && rawEl.jquery ? rawEl.get(0) : rawEl;
                    const id = (el && el.getAttribute('data-module-id')) || item.id;
                    const module = this.modules.find((m) => m.id === id);
                    if (!module) {
                        return;
                    }
                    module.layout[this.activeBreakpoint] = normalizeBreakpointLayout({
                        col: item.width,
                        offset: item.x,
                        y: item.y,
                        h: item.height,
                    });
                });
                this.$nextTick(() => this.refreshAllImageOverflow());
            });
            this.grid.on('resizestop', () => {
                this.$nextTick(() => this.refreshAllImageOverflow());
            });
        },
        refreshAllImageOverflow() {
            if (!this.$refs.grid) {
                return;
            }
            this.modules.forEach((module) => {
                if (module.type !== 'image' || !module.data.url) {
                    return;
                }
                const img = this.$refs.grid.querySelector(
                    `.grid-stack-item[data-module-id="${module.id}"] .page-module-image-cover`
                );
                if (img) {
                    this.updateImageOverflow(module, img);
                }
            });
        },
        destroyGrid() {
            if (this.grid) {
                try {
                    this.grid.destroy(false);
                } catch (e) {
                    // grid already gone
                }
            }
            this.grid = null;
        },
        scheduleGridRebuild() {
            clearTimeout(this.rebuildTimer);
            this.rebuildTimer = setTimeout(() => {
                this.gridEpoch += 1;
                this.$nextTick(() => this.initGrid());
            }, 50);
        },
        onBreakpointChange() {
            this.scheduleGridRebuild();
        },
        modulesForSave() {
            return [...this.modules].sort((a, b) => {
                const la = a.layout.desktop || a.layout[this.activeBreakpoint];
                const lb = b.layout.desktop || b.layout[this.activeBreakpoint];
                if ((la.y || 0) !== (lb.y || 0)) {
                    return (la.y || 0) - (lb.y || 0);
                }
                return (la.offset || 0) - (lb.offset || 0);
            });
        },
        onWidgetMouseDown(event) {
            this.mouseDownPos = { x: event.clientX, y: event.clientY };
            this.interactionMoved = false;
        },
        onWidgetClick(module, event) {
            if (event.target.closest('.page-module-widget-remove')) {
                return;
            }
            if (event.target.closest('.page-module-image-cover') && this.interactionMoved) {
                return;
            }
            if (this.mouseDownPos) {
                const dx = Math.abs(event.clientX - this.mouseDownPos.x);
                const dy = Math.abs(event.clientY - this.mouseDownPos.y);
                if (dx > 5 || dy > 5 || this.interactionMoved) {
                    return;
                }
            }
            this.openModal(module);
        },
        clampObjectPosition(value) {
            const num = Number(value);
            if (Number.isNaN(num)) {
                return 50;
            }
            return Math.max(0, Math.min(100, num));
        },
        imageObjectPositionStyle(module) {
            const x = this.clampObjectPosition(module?.data?.object_position_x ?? 50);
            const y = this.clampObjectPosition(module?.data?.object_position_y ?? 50);
            return {
                objectPosition: `${x}% ${y}%`,
            };
        },
        imageCanPan(module) {
            const overflow = this.imageOverflowById[module.id];
            return !!(overflow && (overflow.x || overflow.y));
        },
        updateImageOverflow(module, imgEl) {
            if (!imgEl || !module) {
                return;
            }
            const frame = imgEl.parentElement;
            if (!frame) {
                return;
            }
            const naturalW = imgEl.naturalWidth;
            const naturalH = imgEl.naturalHeight;
            const boxW = frame.clientWidth;
            const boxH = frame.clientHeight;
            if (!naturalW || !naturalH || !boxW || !boxH) {
                this.imageOverflowById = {
                    ...this.imageOverflowById,
                    [module.id]: { x: false, y: false, rangeX: 0, rangeY: 0 },
                };
                return;
            }
            const scale = Math.max(boxW / naturalW, boxH / naturalH);
            const renderedW = naturalW * scale;
            const renderedH = naturalH * scale;
            const rangeX = Math.max(0, renderedW - boxW);
            const rangeY = Math.max(0, renderedH - boxH);
            this.imageOverflowById = {
                ...this.imageOverflowById,
                [module.id]: {
                    x: rangeX > 0.5,
                    y: rangeY > 0.5,
                    rangeX,
                    rangeY,
                },
            };
        },
        onImagePreviewLoad(module, event) {
            this.updateImageOverflow(module, event.target);
        },
        onImageFocalPointerDown(module, event) {
            event.stopPropagation();
            event.preventDefault();

            if (typeof module.data.object_position_x !== 'number') {
                module.data.object_position_x = this.clampObjectPosition(module.data.object_position_x ?? 50);
            }
            if (typeof module.data.object_position_y !== 'number') {
                module.data.object_position_y = this.clampObjectPosition(module.data.object_position_y ?? 50);
            }

            this.updateImageOverflow(module, event.currentTarget);
            const overflow = this.imageOverflowById[module.id];
            if (!overflow || (!overflow.x && !overflow.y)) {
                return;
            }

            const startX = event.clientX;
            const startY = event.clientY;
            const startPosX = this.clampObjectPosition(module.data.object_position_x);
            const startPosY = this.clampObjectPosition(module.data.object_position_y);
            const pointerId = event.pointerId;

            this.focalDragModuleId = module.id;
            this.interactionMoved = false;
            this.mouseDownPos = { x: startX, y: startY };

            if (event.currentTarget.setPointerCapture) {
                event.currentTarget.setPointerCapture(pointerId);
            }

            const onMove = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;
                if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                    this.interactionMoved = true;
                }

                if (overflow.x && overflow.rangeX > 0) {
                    module.data.object_position_x = this.clampObjectPosition(
                        startPosX + ((-dx / overflow.rangeX) * 100)
                    );
                }
                if (overflow.y && overflow.rangeY > 0) {
                    module.data.object_position_y = this.clampObjectPosition(
                        startPosY + ((-dy / overflow.rangeY) * 100)
                    );
                }
            };

            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
                this.focalDragModuleId = null;
            };

            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
        },
        openModal(module) {
            this.editingId = module.id;
            this.draft = JSON.parse(JSON.stringify(module));
            this.uploadError = null;
            this.libraryError = null;

            if (this.draft.type === 'image') {
                const source = this.draft.data.source
                    || (this.draft.data.media_id ? 'library' : 'url');
                this.imageSourceMode = ['url', 'library', 'upload'].includes(source) ? source : 'url';
                this.draft.data.source = this.imageSourceMode;
                this.draft.data.object_position_x = this.clampObjectPosition(
                    this.draft.data.object_position_x ?? 50
                );
                this.draft.data.object_position_y = this.clampObjectPosition(
                    this.draft.data.object_position_y ?? 50
                );
                this.selectedLibraryMediaId = this.draft.data.media_id || null;
                if (this.imageSourceMode === 'library') {
                    this.loadLibraryImages();
                }
            }

            if (this.draft.type === 'accordion') {
                if (!Array.isArray(this.draft.data.items)) {
                    this.draft.data.items = [];
                }
            }

            if (this.draft.type === 'faq') {
                if (!Array.isArray(this.draft.data.category_ids)) {
                    this.draft.data.category_ids = [];
                }
                this.draftCategoryIds = this.draft.data.category_ids.map((id) => String(id));
            } else {
                this.draftCategoryIds = [];
            }

            this.$nextTick(() => {
                if (this.$refs.modal && window.$) {
                    $(this.$refs.modal)
                        .off('shown.bs.modal.pageModules')
                        .on('shown.bs.modal.pageModules', () => this.initModalSummernote())
                        .on('hidden.bs.modal.pageModules', () => this.destroyModalSummernote());
                    $(this.$refs.modal).modal('show');
                }
            });
        },
        closeModal() {
            if (this.$refs.modal && window.$) {
                $(this.$refs.modal).modal('hide');
            }
            this.draft = null;
            this.editingId = null;
            this.uploadError = null;
            this.uploadInProgress = false;
            this.draftCategoryIds = [];
        },
        saveModal() {
            if (!this.draft || !this.editingId) {
                return;
            }

            if (this.draft.type === 'text' && this.$refs.modalSummernote && window.$ && $(this.$refs.modalSummernote).data('summernote')) {
                this.draft.data.body = $(this.$refs.modalSummernote).summernote('code');
            }

            if (this.draft.type === 'accordion' && window.$) {
                this.syncAccordionSummernotes();
            }

            if (this.draft.type === 'image') {
                this.draft.data.source = this.imageSourceMode;
                if (this.imageSourceMode === 'url') {
                    this.draft.data.media_id = null;
                }
                this.draft.data.object_position_x = this.clampObjectPosition(
                    this.draft.data.object_position_x ?? 50
                );
                this.draft.data.object_position_y = this.clampObjectPosition(
                    this.draft.data.object_position_y ?? 50
                );
            }

            if (this.draft.type === 'faq') {
                this.draft.data.category_ids = (this.draftCategoryIds || [])
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !Number.isNaN(id) && id > 0);
            }

            const index = this.modules.findIndex((m) => m.id === this.editingId);
            if (index !== -1) {
                // Keep existing layout from visual grid (do not overwrite from modal)
                const currentLayout = this.modules[index].layout;
                const updated = JSON.parse(JSON.stringify(this.draft));
                updated.layout = currentLayout;
                this.modules.splice(index, 1, updated);
            }

            this.closeModal();
            this.scheduleGridRebuild();
        },
        addAccordionItem() {
            if (!this.draft || this.draft.type !== 'accordion') {
                return;
            }
            this.syncAccordionSummernotes();
            this.destroyAccordionSummernotes();
            this.draft.data.items.push({
                id: uuid(),
                title: '',
                body: '',
            });
            this.$nextTick(() => this.initAccordionSummernotes());
        },
        removeAccordionItem(index) {
            if (!this.draft || this.draft.type !== 'accordion') {
                return;
            }
            this.syncAccordionSummernotes();
            this.destroyAccordionSummernotes();
            this.draft.data.items.splice(index, 1);
            this.$nextTick(() => this.initAccordionSummernotes());
        },
        moveAccordionItem(index, direction) {
            if (!this.draft || this.draft.type !== 'accordion') {
                return;
            }
            const target = index + direction;
            if (target < 0 || target >= this.draft.data.items.length) {
                return;
            }
            this.syncAccordionSummernotes();
            this.destroyAccordionSummernotes();
            const items = this.draft.data.items;
            const tmp = items[index];
            items.splice(index, 1);
            items.splice(target, 0, tmp);
            this.$nextTick(() => this.initAccordionSummernotes());
        },
        syncAccordionSummernotes() {
            if (!this.draft || this.draft.type !== 'accordion' || !window.$ || !this.$refs.modal) {
                return;
            }
            $(this.$refs.modal).find('.accordion-item-summernote').each((_, el) => {
                if (!$(el).data('summernote')) {
                    return;
                }
                const itemIndex = parseInt(el.getAttribute('data-item-index'), 10);
                if (Number.isNaN(itemIndex) || !this.draft.data.items[itemIndex]) {
                    return;
                }
                this.draft.data.items[itemIndex].body = $(el).summernote('code');
            });
        },
        initAccordionSummernotes() {
            if (!this.draft || this.draft.type !== 'accordion' || !window.$ || !$.fn.summernote || !this.$refs.modal) {
                return;
            }
            const draft = this.draft;
            $(this.$refs.modal).find('.accordion-item-summernote').each((_, el) => {
                if ($(el).data('summernote')) {
                    return;
                }
                const itemIndex = parseInt(el.getAttribute('data-item-index'), 10);
                $(el).summernote({
                    height: 160,
                    toolbar: [
                        ['style', ['style']],
                        ['font', ['bold', 'underline', 'clear']],
                        ['color', ['color']],
                        ['para', ['ul', 'ol', 'paragraph']],
                        ['insert', ['link']],
                        ['view', ['codeview']],
                    ],
                    callbacks: {
                        onChange(content) {
                            if (draft.data.items[itemIndex]) {
                                draft.data.items[itemIndex].body = content;
                            }
                        },
                    },
                });
                const body = draft.data.items[itemIndex] ? draft.data.items[itemIndex].body : '';
                $(el).summernote('code', body || '');
            });
        },
        destroyAccordionSummernotes() {
            if (!window.$ || !this.$refs.modal) {
                return;
            }
            $(this.$refs.modal).find('.accordion-item-summernote').each((_, el) => {
                if ($(el).data('summernote')) {
                    $(el).summernote('destroy');
                }
            });
        },
        csrfToken() {
            const meta = document.querySelector('meta[name="csrf-token"]');
            return meta ? meta.getAttribute('content') : '';
        },
        onImageUrlInput() {
            if (!this.draft) {
                return;
            }
            this.draft.data.media_id = null;
            this.selectedLibraryMediaId = null;
            this.draft.data.source = 'url';
        },
        async loadLibraryImages() {
            if (!this.mediaLibraryUrl) {
                this.libraryError = this.labels.module_image_library_unavailable || 'URL media library non configurato';
                return;
            }
            if (this.libraryLoading) {
                return;
            }
            this.libraryLoading = true;
            this.libraryError = null;
            try {
                const response = await fetch(this.mediaLibraryUrl, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });
                if (!response.ok) {
                    throw new Error('HTTP ' + response.status);
                }
                const payload = await response.json();
                this.libraryImages = Array.isArray(payload.data) ? payload.data : [];
                this.libraryLoaded = true;
            } catch (e) {
                this.libraryError = this.labels.module_image_library_error || 'Impossibile caricare le immagini';
                this.libraryImages = [];
            } finally {
                this.libraryLoading = false;
            }
        },
        onLibrarySelect() {
            if (!this.draft) {
                return;
            }
            const selected = this.libraryImages.find(
                (image) => String(image.id) === String(this.selectedLibraryMediaId)
            );
            if (!selected) {
                this.draft.data.media_id = null;
                this.draft.data.url = null;
                return;
            }
            this.draft.data.source = 'library';
            this.draft.data.media_id = selected.id;
            this.draft.data.url = selected.url;
        },
        async onImageUpload(event) {
            const file = event.target.files && event.target.files[0];
            if (!file || !this.draft) {
                return;
            }
            if (!this.uploadUrl) {
                this.uploadError = this.labels.module_image_upload_unavailable || 'Upload non configurato';
                return;
            }

            this.uploadInProgress = true;
            this.uploadError = null;

            const formData = new FormData();
            formData.append('_token', this.csrfToken());
            formData.append('operation', this.operation || 'create');
            formData.append('model', this.pageModel);
            if (this.pageId) {
                formData.append('model_id', this.pageId);
            }
            formData.append('collection', 'module_media');
            formData.append('files', file);

            try {
                const response = await fetch(this.uploadUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': this.csrfToken(),
                    },
                    credentials: 'same-origin',
                    body: formData,
                });
                const payload = await response.json();
                const uploaded = payload.files && payload.files[0];
                if (!uploaded || uploaded.error) {
                    throw new Error((uploaded && uploaded.error) || 'Upload fallito');
                }
                this.draft.data.source = 'upload';
                this.draft.data.media_id = uploaded.id;
                this.draft.data.url = uploaded.url || uploaded.thumbnailUrl || null;
                this.selectedLibraryMediaId = null;
            } catch (e) {
                this.uploadError = e.message || (this.labels.module_image_upload_error || 'Upload fallito');
            } finally {
                this.uploadInProgress = false;
                event.target.value = '';
            }
        },
        removeEditingModule() {
            if (!this.editingId) {
                return;
            }
            const id = this.editingId;
            this.closeModal();
            this.removeModuleById(id);
        },
        initModalSummernote() {
            if (!this.draft) {
                return;
            }

            if (this.draft.type === 'accordion') {
                this.initAccordionSummernotes();
                return;
            }

            if (this.draft.type !== 'text') {
                return;
            }
            if (!this.$refs.modalSummernote || !window.$ || !$.fn.summernote) {
                return;
            }

            this.destroyModalSummernote();
            const draft = this.draft;
            $(this.$refs.modalSummernote).summernote({
                height: 220,
                toolbar: [
                    ['style', ['style']],
                    ['font', ['bold', 'underline', 'clear']],
                    ['fontname', ['fontname']],
                    ['color', ['color']],
                    ['para', ['ul', 'ol', 'paragraph']],
                    ['table', ['table']],
                    ['insert', ['link']],
                    ['view', ['fullscreen', 'codeview']],
                ],
                callbacks: {
                    onChange(content) {
                        draft.data.body = content;
                    },
                },
            });
            $(this.$refs.modalSummernote).summernote('code', draft.data.body || '');
            this.modalSummernoteReady = true;
        },
        destroyModalSummernote() {
            this.destroyAccordionSummernotes();
            if (this.$refs.modalSummernote && window.$ && $(this.$refs.modalSummernote).data('summernote')) {
                $(this.$refs.modalSummernote).summernote('destroy');
            }
            this.modalSummernoteReady = false;
        },
        addModule(type) {
            const module = {
                id: uuid(),
                type,
                layout: {
                    mobile: { ...DEFAULT_LAYOUT.mobile },
                    small: { ...DEFAULT_LAYOUT.small },
                    desktop: { ...DEFAULT_LAYOUT.desktop },
                },
                data: emptyData(type),
            };

            let maxY = 0;
            this.modules.forEach((m) => {
                const layout = m.layout[this.activeBreakpoint];
                maxY = Math.max(maxY, (layout.y || 0) + (layout.h || 2));
            });
            const fullWidth = ['text', 'html', 'accordion', 'faq'].includes(type);
            module.layout[this.activeBreakpoint] = normalizeBreakpointLayout({
                col: fullWidth ? 12 : 6,
                offset: 0,
                y: maxY,
                h: 4,
            });

            this.modules.push(module);
            this.$nextTick(() => {
                this.initGrid();
                this.openModal(module);
            });
        },
        removeModuleById(id) {
            const index = this.modules.findIndex((m) => m.id === id);
            if (index === -1) {
                return;
            }
            this.destroyGrid();
            this.modules.splice(index, 1);
            this.$nextTick(() => this.initGrid());
        },
        previewSnippet(module) {
            if (module.type === 'text' || module.type === 'html') {
                const body = module.data.body || '';
                const plain = body.replace(/<[^>]+>/g, ' ').trim();
                return plain.length > 90 ? plain.substring(0, 90) + '…' : plain || '<em>—</em>';
            }
            if (module.type === 'image') {
                return module.data.media_id ? 'media #' + module.data.media_id : '—';
            }
            if (module.type === 'video') {
                return module.data.url || (module.data.media_id ? 'media #' + module.data.media_id : '—');
            }
            if (module.type === 'accordion') {
                const count = Array.isArray(module.data.items) ? module.data.items.length : 0;
                return count + ' ' + (count === 1 ? 'voce' : 'voci');
            }
            if (module.type === 'faq') {
                const ids = Array.isArray(module.data.category_ids) ? module.data.category_ids : [];
                if (!ids.length) {
                    return 'Tutte le categorie';
                }
                return ids.length + ' categor' + (ids.length === 1 ? 'ia' : 'ie');
            }
            return '';
        },
    },
};
</script>

<style scoped>
.page-modules-editor {
    color: #222;
}
.page-modules-toolbar {
    margin-bottom: 10px;
}
.page-modules-grid {
    min-height: 220px;
    background:
        linear-gradient(to right, rgba(0, 0, 0, 0.06) 1px, transparent 1px) 0 0 / calc(100% / 12) 100%,
        #f7f7f7;
    border: 1px dashed #c5c5c5;
    padding: 8px;
    margin-bottom: 15px;
    color: #222;
}
.page-modules-grid :deep(.grid-stack-item) {
    min-height: 80px;
    color: #222;
}
.page-modules-grid :deep(.ui-resizable-se) {
    width: 16px !important;
    height: 16px !important;
    right: 2px !important;
    bottom: 2px !important;
    background: #3c8dbc !important;
    border-radius: 2px;
    opacity: 0.85;
}
.page-module-widget {
    background: #fff;
    border: 1px solid #d2d6de;
    border-radius: 3px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    height: 100%;
    min-height: 70px;
    padding: 8px 10px;
    cursor: move;
    overflow: hidden;
    position: relative;
    color: #222 !important;
}
.page-module-widget:hover {
    border-color: #3c8dbc;
}
.page-module-widget--image {
    padding: 0;
    display: flex;
    flex-direction: column;
}
.page-module-widget-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    padding: 5px 8px;
    border-radius: 2px;
    background: #3c8dbc;
    color: #fff !important;
}
.page-module-widget-header strong {
    color: #fff !important;
    font-weight: 700;
}
.page-module-widget--image .page-module-widget-header {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 6px 8px;
    border-radius: 0;
    background: #3c8dbc;
}
.page-module-widget--image .page-module-widget-meta {
    z-index: 2;
    background: rgba(255, 255, 255, 0.92);
    padding: 2px 6px;
    border-radius: 2px;
    color: #444 !important;
}
.page-module-widget-body {
    font-size: 12px;
    color: #333 !important;
    max-height: 70%;
    overflow: hidden;
}
.page-module-widget-meta {
    position: absolute;
    right: 8px;
    bottom: 6px;
    font-size: 10px;
    color: #555 !important;
}
.page-module-widget-remove {
    margin-left: 6px;
}
.page-module-image-frame {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    background: #f0f0f0;
}
.page-module-image-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 50%;
    display: block;
    user-select: none;
    -webkit-user-drag: none;
    cursor: grab;
}
.page-module-image-cover.is-dragging {
    cursor: grabbing;
}
.page-module-image-cover.no-pan {
    cursor: default;
}
.page-module-image-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 60px;
    font-size: 12px;
}
.page-module-image-modal-full {
    max-width: 100%;
    max-height: 180px;
    border: 1px solid #ddd;
    display: block;
}
.page-module-widget-meta {
    position: absolute;
    right: 8px;
    bottom: 6px;
    font-size: 10px;
}
.page-module-widget-remove {
    margin-left: 6px;
}
</style>
